import { translate } from '../../../lib/i18n'
/**
 * Las piezas que comparten las dos analíticas: las de tráfico y las de ventas.
 *
 * Vivían dentro de `AnalyticsSection`, que era la única. Al llegar Analíticas de
 * ventas hacían falta las mismas tarjetas de cifra con su insignia de
 * tendencia, las mismas listas de «top» y el mismo donut de reparto — y con dos
 * copias, un arreglo en una habría dejado a la otra distinta sin que nadie se
 * enterara hasta verlas juntas.
 *
 * Nada de aquí sabe qué se está midiendo: reciben cifras ya formateadas o el
 * formateador. Es lo que permite que la misma tarjeta enseñe visitas en una
 * pantalla y pesos en la otra.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { IconArrowDownRight, IconArrowUpRight, IconChevronDown, IconInfoCircle, IconUnlink } from '@tabler/icons-react'
/* Sólo para tipar la prop `icon`: cualquier icono de Tabler sirve, y este es el
   que se toma de muestra de esa forma. */
import type { IconUsers } from '@tabler/icons-react'
import { formatMoneyParts } from './offerShared'

/** El marco interior de todas las tarjetas del taller. */
export const panel = {
  background: 'var(--dash-card-muted)',
  border: '1px solid',
  borderTopColor: 'var(--dash-card-muted-border-top)',
  borderRightColor: 'var(--dash-card-muted-border-side)',
  borderBottomColor: 'var(--dash-card-muted-border-bottom)',
  borderLeftColor: 'var(--dash-card-muted-border-side)',
  boxShadow: 'var(--dash-card-muted-shadow)',
  /* La esquina de todas las tarjetas del panel: con 0, las de Analíticas eran
     las únicas rectas junto a las de las fichas y los widgets. */
  borderRadius: 'var(--dash-card-radius)',
} as const

/**
 * Percent change vs the previous period. Returns null when there's nothing to
 * compare against — showing "+∞%" for a site's first days would be noise.
 */
export function trend(current: number, previous: number): { label: string; up: boolean } | null {
  // No comparison data → no badge (previously showed "Nuevo", which was noise).
  if (previous === 0) return null
  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct === 0) return null
  return { label: `${pct > 0 ? '+' : ''}${pct}%`, up: pct > 0 }
}


/**
 * La «i» y su tarjeta negra: lo que explica una cifra sin ocupar sitio bajo
 * ella.
 *
 * Estaba dentro de `AnalyticsSection` colgando de las filas de campañas. Vive
 * aquí porque las tarjetas de cifra la necesitan por el mismo motivo: una
 * definición —«esto no descuenta la comisión de Stripe»— hay que poder leerla
 * una vez y no tenerla delante para siempre.
 *
 * Se renderiza dentro del elemento que la ancla para heredar su contexto de
 * apilado; quien la contenga tiene que levantarse en `:hover` para que la
 * tarjeta pinte por encima de sus vecinos en vez de quedar debajo.
 */
export type InfoStat = { term: string; detail: string }
export type InfoSection = { label: string; rows: { value: string; views: number }[] }

export function RowInfo({ title, sub, note, stats = [], sections = [] }: {
  title: string
  /** Línea monoespaciada bajo el título: una URL, un identificador. */
  sub?: string
  /** Prosa: qué es esta cifra y qué no incluye. */
  note?: string | undefined
  stats?: InfoStat[]
  sections?: InfoSection[]
}) {
  const shownSections = sections.filter((section) => section.rows.length > 0)
  return (
    <div className="dash-row-info">
      <button className="dash-row-info-btn" type="button" aria-label={translate('analytics.infoAbout', { title })}>
        <IconInfoCircle size={13} />
      </button>
      <div className="dash-row-info-card" role="tooltip">
        <span className="dash-row-info-title">{title}</span>
        {sub ? <span className="dash-row-info-sub">{sub}</span> : null}
        {note ? <span className="dash-row-info-note">{note}</span> : null}
        {stats.length > 0 ? (
          <ul className="dash-row-info-list">
            {stats.map((stat) => (
              <li key={stat.term}>
                <span className="dash-row-info-term">{stat.term}</span>
                <span className="dash-row-info-detail">{stat.detail}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {shownSections.length > 0 ? (
          <div className="dash-row-info-sections">
            {shownSections.map((section) => (
              <div className="dash-row-info-section" key={section.label}>
                <span className="dash-row-info-section-label">{section.label}</span>
                {section.rows.map((row) => (
                  <div className="dash-row-info-section-row" key={row.value}>
                    <span className="dash-row-info-section-value">{row.value}</span>
                    <span className="dash-row-info-section-views">{row.views.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}


/**
 * A partir de cuántos caracteres un importe deja de escribirse entero.
 *
 * Un millón con centavos —«1,234,567.89»— son doce caracteres, y encogerlos
 * hasta que quepan los deja en once píxeles: más pequeños que el rótulo que
 * tienen encima, que es cuando la cifra principal deja de parecer la principal.
 * Pasado ese punto se abrevia —«USD 1.2 M»—, que es lo que hace cualquier panel
 * a esa magnitud, y el importe exacto se queda en el `title`.
 */
const COMPACT_FROM = 11

/**
 * Un importe como cifra grande de tarjeta, con la moneda en pequeño.
 *
 * Sólo se encoge cuando la unidad es un código de tres letras —`USD`, `MXN`—,
 * que es lo que desbordaba la tarjeta. Un símbolo suelto como `$` ya es
 * estrecho, y hacerlo diminuto lo dejaría flotando sin ganar nada de ancho.
 *
 * El `title` lleva el importe entero: aunque quepa casi siempre, la tarjeta
 * puede quedarse a un cuarto de rejilla y ahí sigue habiendo puntos
 * suspensivos.
 */
export function MoneyMetric({ amount, currency }: {
  /** En la unidad mínima de la moneda, como todo el comercio. */
  amount: number
  currency: string
}) {
  const exact = formatMoneyParts(amount, currency)
  // Millones: abreviado en la cara, exacto en el `title`.
  const parts = exact.amount.length >= COMPACT_FROM
    ? formatMoneyParts(amount, currency, { compact: true })
    : exact
  /* Un símbolo va pegado al número —«$2,226»— y un código de tres letras
     separado —«USD 2,226»—, que es como los escribe `Intl` de origen. */
  const join = (unit: string, digits: string, unitFirst: boolean) => {
    const space = unit.length <= 1 ? '' : ' '
    return unitFirst ? `${unit}${space}${digits}` : `${digits}${space}${unit}`
  }
  const shown = join(parts.unit, parts.amount, parts.unitFirst)
  const title = join(exact.unit, exact.amount, exact.unitFirst)
  /* Se mide sólo la cifra: la unidad ya va encogida aparte y contarla haría
     saltar un escalón de más a cualquier importe con código de tres letras. */
  const fit = 'dash-metric-fit'
  if (parts.unit.length <= 1) return <span className={fit} title={title}>{shown}</span>
  const tag = <span className="dash-metric-unit">{parts.unit}</span>
  return (
    <span className={fit} title={title}>
      {parts.unitFirst ? <>{tag}{parts.amount}</> : <>{parts.amount}{tag}</>}
    </span>
  )
}

export function StatCard({ label, value, icon: Icon, change, foot, hint, info, loading = false }: {
  label: string
  /** Texto, o `MoneyMetric` cuando la cifra es dinero. */
  value: ReactNode
  icon: typeof IconUsers
  change?: { label: string; up: boolean } | null
  foot?: ReactNode
  /**
   * Un DATO que matiza la cifra: «1 nuevo · 8 que vuelven», «3 ventas con
   * devolución». Va a la vista porque no está en ningún otro sitio de la
   * pantalla. Lo que sea explicación y no dato va en `info`.
   */
  hint?: string | undefined
  /**
   * Lo que se lee una vez: qué mide exactamente la cifra y contra qué tramo se
   * compara. Vive tras la «i» porque con cuatro tarjetas en fila, una frase
   * bajo cada una convierte la fila en un párrafo.
   */
  info?: { note?: string | undefined; rows?: InfoStat[] } | undefined
  loading?: boolean
}) {
  return (
    /* La misma pieza que las métricas de la ficha de una página (`PageStats`):
       el rótulo como el título de una fila del widget de Páginas, y la cifra
       dentro de su caja, el doble fondo de los widgets. */
    <div className="dash-stat-card dash-page-stat" style={{ ...panel, padding: 20 }}>
      <div className="dash-page-stat-head">
        <span style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <span className="dash-page-stat-title">{label}</span>
          {info ? <RowInfo title={label} note={info.note} stats={info.rows ?? []} /> : null}
        </span>
        <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true">
          <Icon size={14} />
        </span>
      </div>
      <div className="dash-page-stat-body">
      <div className="dash-analytics-stat-value">
        {loading ? (
          <span className="sk dash-analytics-stat-skeleton" aria-hidden="true" />
        ) : (
          <span
            /* Un texto suelto se escala aquí; `MoneyMetric` trae su propia
               escala porque sabe separar la cifra de la moneda. */
            /* Sin cifra —«—» o vacío— va con la letra de las descripciones: es
               una ausencia, no un dato grande. */
            className={`dash-analytics-content-in dash-card-metric dash-analytics-metric${value === '—' || value === '' ? ' is-empty' : ''}`}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={typeof value === 'string' ? value : undefined}
          >
            {value}
          </span>
        )}
        {!loading && change ? (
          <span className="dash-analytics-content-in dash-status-label" style={{ color: change.up ? 'var(--dash-accent)' : 'var(--dash-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {change.up ? <IconArrowUpRight size={12} /> : <IconArrowDownRight size={12} />}
            {change.label}
          </span>
        ) : null}
      </div>
      {!loading && hint ? <div className="dash-card-meta">{hint}</div> : null}
      {!loading && foot ? foot : null}
      </div>
    </div>
  )
}

/**
 * El vacío de una tarjeta de Analíticas: el icono de la propia tarjeta y la
 * frase, centrados en su caja.
 *
 * El mismo gesto que el vacío de Formularios («No submissions yet»). Era una
 * frase gris suelta arriba a la izquierda, y en una tarjeta alta eso se leía
 * como una tarjeta a medio cargar y no como «aquí no hay nada todavía».
 */
function StatEmpty({ icon: Icon, text }: { icon: typeof IconUsers; text: string }) {
  return (
    <div className="dash-analytics-content-in dash-stat-empty">
      <Icon size={30} aria-hidden="true" />
      <strong>{text}</strong>
    </div>
  )
}

export type TopListRow = {
  key: string
  label: string
  sub?: string | undefined
  /** La cifra de la derecha, ya escrita: puede ser un número o dinero. */
  value: string
  /** Miniatura a la izquierda del nombre. La usan las ofertas más vendidas. */
  image?: string | null
  info?: ReactNode
  /** Marca la fila como histórica: la página o la oferta ya no existe. */
  deleted?: boolean
  /** Qué dice el aviso de la fila borrada. */
  deletedHint?: string
  /**
   * A dónde lleva el nombre, si la fila es algo que se abre en el panel.
   *
   * Una lista de «lo que más vende» o «quién más reserva» es un índice: se lee
   * para ir a la cosa. Sin esto había que copiar el nombre, cambiar de sección
   * y buscarlo — justo el trayecto que la lista debería ahorrar.
   *
   * Opcional porque no todo lo que se lista es una cosa del panel: un país, una
   * fuente de tráfico o un día de la semana no tienen ficha a la que ir. Y no se
   * ofrece en las filas borradas aunque se pase: llevaría a una ficha que ya no
   * existe.
   */
  onOpen?: (() => void) | undefined
}

export function TopList({ title, icon: Icon, rows, empty, loading = false, fillHeight = false, scrollHint }: {
  title: string
  icon: typeof IconUsers
  rows: TopListRow[]
  empty: string
  loading?: boolean
  /** Match the (taller) sibling's height via the grid's stretch, scrolling the
   *  list inside. No explicit height — the grid row already sizes it, so the flex
   *  body fills exactly the available space without cropping. */
  fillHeight?: boolean
  /** El texto del pie que aparece cuando queda lista por debajo del corte. */
  scrollHint?: string
}) {
  const listBodyRef = useRef<HTMLDivElement>(null)
  const [canScrollMore, setCanScrollMore] = useState(false)
  // Hover cards have to escape the panel, so a list that has them cannot clip.
  const hasInfo = rows.some((row) => Boolean(row.info) || row.deleted)

  useEffect(() => {
    if (!fillHeight) {
      setCanScrollMore(false)
      return
    }
    const body = listBodyRef.current
    if (!body) return
    const update = () => setCanScrollMore(body.scrollHeight - body.scrollTop - body.clientHeight > 2)
    const frame = window.requestAnimationFrame(update)
    const observer = new ResizeObserver(update)
    observer.observe(body)
    return () => {
      window.cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [fillHeight, loading, rows.length])

  return (
    <div className={`dash-analytics-list-panel${fillHeight ? ' is-fill-height' : ''}`} style={{ ...panel, overflow: hasInfo ? 'visible' : 'hidden', ...(fillHeight ? { display: 'flex', flexDirection: 'column', minHeight: 0 } : {}) }}>
      <div style={{ padding: '16px 20px 4px', flexShrink: 0 }}>
        <div className="dash-analytics-card-heading">
          <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true"><Icon size={14} /></span>
          <h3 className="dash-card-title">{title}</h3>
        </div>
      </div>
      <div ref={listBodyRef} onScroll={fillHeight ? () => {
        const body = listBodyRef.current
        if (body) setCanScrollMore(body.scrollHeight - body.scrollTop - body.clientHeight > 2)
      } : undefined} className="dash-analytics-list-body" style={fillHeight ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}>
        {loading ? (
          Array.from({ length: 5 }, (_, index) => (
            <div className="dash-analytics-list-skeleton-row" key={index} aria-hidden="true">
              <span className="sk" />
              <span className="sk" />
            </div>
          ))
        ) : rows.length === 0 ? (
          <StatEmpty icon={Icon} text={empty} />
        ) : (
          rows.map((row) => (
            <div
              key={row.key}
              className="dash-analytics-list-row dash-analytics-content-in"
            >
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {row.info}
                {row.image ? (
                  <img className="dash-analytics-row-thumb" src={row.image} alt="" loading="lazy" />
                ) : null}
                <div style={{ minWidth: 0 }}>
                  {row.onOpen && !row.deleted ? (
                    /* Un botón y no un enlace: no lleva a una dirección sino a
                       una sección del panel, y así se activa con el teclado
                       igual que cualquier otro mando. */
                    <button
                      type="button"
                      className="dash-card-primary dash-analytics-row-link"
                      onClick={row.onOpen}
                    >
                      {row.label}
                    </button>
                  ) : (
                    <div className="dash-card-primary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.label}
                    </div>
                  )}
                  {row.sub && row.sub !== row.label ? (
                    <div className="dash-card-meta" style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                      {row.deleted ? (
                        <span
                          className="dash-deleted-page-icon"
                          data-tooltip={row.deletedHint ?? translate('analytics.pageDeleted')}
                          role="img"
                          aria-label={row.deletedHint ?? translate('analytics.pageDeleted')}
                          tabIndex={0}
                        >
                          <IconUnlink size={13} />
                        </span>
                      ) : null}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.sub}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <span className="dash-card-meta dash-analytics-views-value" style={{ flexShrink: 0 }}>{row.value}</span>
            </div>
          ))
        )}
      </div>
      {fillHeight && canScrollMore && scrollHint ? (
        <div className="dash-analytics-scroll-hint" aria-hidden="true">
          <span>{scrollHint}</span>
          <IconChevronDown size={13} />
        </div>
      ) : null}
    </div>
  )
}

/**
 * Un reparto como donut. Part-to-whole, pocos segmentos, leído de un vistazo —
 * el único trabajo que un donut hace bien.
 *
 * El color va atado a la identidad del segmento y nunca a su posición: reordenar
 * o que uno desaparezca no puede repintar a los que quedan. Los segmentos se
 * dibujan en el orden en que llegan, por la misma razón — quien llama decide ese
 * orden una vez y no lo cambia al cambiar las cifras.
 */
const DONUT_SIZE = 176
const DONUT_R_OUTER = 82
const DONUT_R_INNER = 56
/** Surface gap between touching segments — a real gap, not a stroke around the mark. */
const DONUT_GAP_PX = 2
/** How far the hovered segment grows, so the mark visibly responds. */
const DONUT_LIFT = 4
/**
 * Smallest sweep a non-zero segment may render at. Without a floor, a slice
 * with a handful of units produces an arc thinner than the gap carved for it —
 * it vanishes while the legend still lists it, leaving an orphan notch in the
 * ring. The shortfall is taken back proportionally from the larger segments so
 * the sweeps still sum to a full turn.
 */
const DONUT_MIN_SWEEP = 0.045

function polar(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }
}

function donutArc(cx: number, cy: number, rOuter: number, rInner: number, a0: number, a1: number): string {
  const largeArc = a1 - a0 > Math.PI ? 1 : 0
  const o0 = polar(cx, cy, rOuter, a0)
  const o1 = polar(cx, cy, rOuter, a1)
  const i1 = polar(cx, cy, rInner, a1)
  const i0 = polar(cx, cy, rInner, a0)
  return [
    `M ${o0.x.toFixed(2)} ${o0.y.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${o1.x.toFixed(2)} ${o1.y.toFixed(2)}`,
    `L ${i1.x.toFixed(2)} ${i1.y.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${i0.x.toFixed(2)} ${i0.y.toFixed(2)}`,
    'Z',
  ].join(' ')
}

export type DonutSlice = { key: string; label: string; value: number; color: string }

export function SplitDonut({ title, icon: Icon, slices, empty, unit, formatValue, loading = false }: {
  title: string
  icon: typeof IconUsers
  /** Ya en el orden en que deben dibujarse. Los de valor cero se descartan. */
  slices: DonutSlice[]
  empty: string
  /** Cómo se nombra lo medido para el lector de pantalla: "vistas", "ventas". */
  unit: string
  /** El valor crudo de la leyenda. Por defecto, número suelto. */
  formatValue?: (value: number) => string
  loading?: boolean
}) {
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const write = formatValue ?? ((value: number) => value.toLocaleString())

  const ordered = slices.filter((slice) => slice.value > 0)
  const total = ordered.reduce((sum, slice) => sum + slice.value, 0)

  const pctOf = (value: number) => (total > 0 ? (value / total) * 100 : 0)
  /**
   * Rounding must not contradict the list: a slice with real volume can't read
   * "0%", and no single slice reads "100%" while others are still listed.
   */
  const pctLabel = (value: number) => {
    const pct = pctOf(value)
    if (pct > 0 && pct < 1) return '<1%'
    const rounded = Math.round(pct)
    if (rounded === 100 && ordered.length > 1) return '99%'
    return `${rounded}%`
  }

  // El centro arranca con el segmento mayor y sigue al puntero o al foco.
  const leader = ordered.reduce<DonutSlice | null>(
    (best, slice) => (best === null || slice.value > best.value ? slice : best),
    null
  )
  const shown = (activeKey ? ordered.find((slice) => slice.key === activeKey) : null) ?? leader

  const cx = DONUT_SIZE / 2
  const cy = DONUT_SIZE / 2
  // Un solo segmento no tiene nada que separar, así que se dibuja como un anillo.
  const single = ordered.length === 1
  const gapAngle = single ? 0 : DONUT_GAP_PX / DONUT_R_OUTER

  const floorSweep = single ? 0 : gapAngle + DONUT_MIN_SWEEP
  const rawSweeps = ordered.map((slice) => (slice.value / total) * Math.PI * 2)
  // Borrow what the floored segments need from those that can spare it.
  const deficit = rawSweeps.reduce((sum, sweep) => sum + (sweep < floorSweep ? floorSweep - sweep : 0), 0)
  const donorTotal = rawSweeps.reduce((sum, sweep) => sum + (sweep >= floorSweep ? sweep : 0), 0)
  const sweeps = rawSweeps.map((sweep) =>
    sweep < floorSweep ? floorSweep : donorTotal > 0 ? sweep - deficit * (sweep / donorTotal) : sweep
  )

  let cursor = -Math.PI / 2 // 12 o'clock
  const segments = ordered.map((slice, index) => {
    const sweep = sweeps[index] ?? 0
    const a0 = cursor + gapAngle / 2
    const a1 = cursor + sweep - gapAngle / 2
    cursor += sweep
    return { ...slice, a0, a1, valid: a1 > a0 }
  })

  return (
    <div className="dash-split-donut" style={{ ...panel, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px 4px' }}>
        <div className="dash-analytics-card-heading">
          <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true"><Icon size={14} /></span>
          <h3 className="dash-card-title">{title}</h3>
        </div>
      </div>

      {/* El contenido en su caja, bajo el título: el doble fondo de los widgets. */}
      <div className="dash-stat-inset">
      {loading ? (
        <div className="dash-analytics-list-body">
          {Array.from({ length: 3 }, (_, index) => (
            <div className="dash-analytics-list-skeleton-row" key={index} aria-hidden="true">
              <span className="sk" />
              <span className="sk" />
            </div>
          ))}
        </div>
      ) : total === 0 ? (
        <StatEmpty icon={Icon} text={empty} />
      ) : (
        <div className="dash-analytics-content-in dash-device-donut">
          <svg
            width={DONUT_SIZE}
            height={DONUT_SIZE}
            viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
            role="img"
            aria-label={`${title}: ${ordered.map((slice) => `${slice.label} ${pctLabel(slice.value)}`).join(', ')}.`}
            style={{ display: 'block', flexShrink: 0, overflow: 'visible' }}
          >
            {single && segments[0] ? (
              <circle
                cx={cx}
                cy={cy}
                r={(DONUT_R_OUTER + DONUT_R_INNER) / 2}
                fill="none"
                stroke={segments[0].color}
                strokeWidth={DONUT_R_OUTER - DONUT_R_INNER}
              />
            ) : (
              segments.filter((segment) => segment.valid).map((segment) => {
                const isActive = activeKey === segment.key
                return (
                  <path
                    key={segment.key}
                    d={donutArc(
                      cx,
                      cy,
                      isActive ? DONUT_R_OUTER + DONUT_LIFT : DONUT_R_OUTER,
                      DONUT_R_INNER,
                      segment.a0,
                      segment.a1
                    )}
                    fill={segment.color}
                    tabIndex={0}
                    role="button"
                    aria-label={`${segment.label}: ${write(segment.value)} ${unit}, ${pctLabel(segment.value)}`}
                    style={{ cursor: 'pointer', outline: 'none', transition: 'd 140ms ease' }}
                    onMouseEnter={() => setActiveKey(segment.key)}
                    onMouseLeave={() => setActiveKey(null)}
                    onFocus={() => setActiveKey(segment.key)}
                    onBlur={() => setActiveKey(null)}
                  />
                )
              })
            )}

            {/* Stationary readout: the value leads, the label follows. */}
            {shown ? (
              <>
                <text x={cx} y={cy - 4} textAnchor="middle" dominantBaseline="middle" fill="var(--dash-text)" fontSize={26} fontWeight={600}>
                  {pctLabel(shown.value)}
                </text>
                <text x={cx} y={cy + 18} textAnchor="middle" dominantBaseline="middle" fill="var(--dash-muted)" fontSize={12}>
                  {shown.label}
                </text>
              </>
            ) : null}
          </svg>

          {/* Always-present legend: identity is never carried by colour alone, and
              every value stays reachable without hovering. */}
          <ul className="dash-device-legend">
            {ordered.map((slice) => (
              <li
                key={slice.key}
                className={`dash-device-legend-row${activeKey === slice.key ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveKey(slice.key)}
                onMouseLeave={() => setActiveKey(null)}
              >
                <span className="dash-device-swatch" style={{ background: slice.color }} aria-hidden="true" />
                <span className="dash-device-legend-name">{slice.label}</span>
                <span className="dash-device-legend-value">
                  {pctLabel(slice.value)}
                  <span className="dash-device-legend-views dash-analytics-views-value">{write(slice.value)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      </div>
    </div>
  )
}
